import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { db } from "../../firebase";
import { collection, getDocs } from "firebase/firestore";
import { Search, UserCircle, X, ChevronLeft } from "lucide-react";

const SearchFriends = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [results, setResults] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const usersRef = collection(db, "Users");
        const snapshot = await getDocs(usersRef);
        const usersData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setAllUsers(usersData);
      } catch (error) {
        console.error("Error fetching user data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (searchQuery.trim() === "") {
      setResults([]);
      return;
    }
    const filteredUsers = allUsers.filter((user) => {
      const searchLower = searchQuery.toLowerCase();
      const fullName = `${user.nombre} ${user.apellido}`.toLowerCase();
      const nickNameMatch = user.nickname?.toLowerCase().includes(searchLower);
      return fullName.includes(searchLower) || nickNameMatch;
    });
    setResults(filteredUsers);
  }, [searchQuery, allUsers]);

  const clearSearch = () => {
    setSearchQuery("");
  };

  const LoadingSkeleton = () => (
    <div className="space-y-4">
      {[...Array(5)].map((_, index) => (
        <div
          key={index}
          className="bg-white p-4 rounded-xl animate-pulse flex items-center space-x-4"
        >
          <div className="w-12 h-12 bg-gray-200 rounded-full" />
          <div className="flex-1 space-y-2">
            <div className="h-4 bg-gray-200 rounded w-3/4" />
            <div className="h-3 bg-gray-200 rounded w-1/2" />
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white pt-16">
      {/* Header with back button */}
      <div className="sticky top-16 bg-white/80 backdrop-blur-lg shadow-sm z-40 p-4">
        <div className="max-w-2xl mx-auto">
          {/* Back and Title */}
          <div className="flex items-center mb-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <ChevronLeft className="w-6 h-6 text-gray-600" />
            </button>
            <h1 className="text-xl font-bold text-gray-800 ml-2">
              Buscar Amigos
            </h1>
          </div>

          {/* Search Bar */}
          <div className="relative">
            <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
              <Search className="w-5 h-5 text-gray-400" />
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Buscar por nombre o nickname..."
              className="w-full pl-10 pr-10 py-3 bg-white rounded-xl border-none focus:ring-2 focus:ring-orange-500 transition-shadow shadow-sm"
            />
            {searchQuery && (
              <button
                onClick={clearSearch}
                className="absolute inset-y-0 right-3 flex items-center text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-2xl mx-auto p-4">
        {loading ? (
          <LoadingSkeleton />
        ) : (
          <div className="space-y-3">
            {/* Initial State */}
            {searchQuery.trim() === "" && (
              <div className="bg-white p-6 rounded-xl text-center space-y-2 shadow-sm">
                <h3 className="text-lg font-semibold text-gray-800">
                  ¡Busca a tus amigos y conéctate!
                </h3>
                <p className="text-gray-600">
                  Escribe el nombre, apellido o nickname de tus amigos para
                  encontrarlos.
                </p>
              </div>
            )}

            {/* No Results */}
            {results.length === 0 && searchQuery.trim() !== "" && (
              <div className="bg-white p-4 rounded-xl text-center text-gray-500">
                No se encontraron resultados
              </div>
            )}

            {/* User Results */}
            {searchQuery.trim() !== "" &&
              results.map((user) => (
                <div
                  key={user.id}
                  onClick={() => navigate(`/user/${user.id}`)}
                  className="bg-white p-4 rounded-xl shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer flex items-center space-x-4 hover:scale-[1.01]"
                >
                  <div className="relative">
                    {user.photoUrl ? (
                      <img
                        src={user.photoUrl}
                        alt={`${user.nombre} ${user.apellido}`}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                        <UserCircle className="w-8 h-8 text-orange-500" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-medium text-gray-900">
                      {`${user.nombre} ${user.apellido}`}
                    </h3>
                    {user.nickname && (
                      <p className="text-sm text-gray-500">@{user.nickname}</p>
                    )}
                  </div>
                </div>
              ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchFriends;
